import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import { compactPagesFor, compactPath } from "@/data/compact";
import type { CompactKind, CompactVertical } from "@/data/compact/types";

interface CompactLinkGridProps {
  vertical: CompactVertical;
  kind?: CompactKind;
  title: string;
  intro?: string;
  /** Slug som inte ska listas (sidan man står på). */
  exclude?: string;
  /** Visa högst så här många länkar, utan grupper (t.ex. på kommunsidor). */
  limit?: number;
  className?: string;
}

/**
 * Länklista till compact-sidorna i en vertikal, grupperad efter kluster.
 * Används på hubbsidor, kommunsidor och compact-sidorna själva, så att varje
 * sida får interna länkar från sin hubb och sina syskon.
 */
const CompactLinkGrid = ({ vertical, kind, title, intro, exclude, limit, className }: CompactLinkGridProps) => {
  const pages = compactPagesFor(vertical, kind).filter((p) => p.slug !== exclude);
  if (!pages.length) return null;

  const visible = limit ? pages.slice(0, limit) : pages;
  const groups = limit
    ? [{ name: "", items: visible }]
    : [...new Set(visible.map((p) => p.group))].map((name) => ({
        name,
        items: visible.filter((p) => p.group === name),
      }));

  return (
    <section className={className} aria-labelledby={`compact-${vertical}-${kind ?? "alla"}`}>
      <h2 id={`compact-${vertical}-${kind ?? "alla"}`} className="heading-2 mb-2">
        {title}
      </h2>
      {intro && <p className="mb-6 max-w-3xl text-muted-foreground">{intro}</p>}
      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g.name || "alla"}>
            {g.name && <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{g.name}</h3>}
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={compactPath(p)}
                    className="group flex min-h-11 items-center justify-between gap-3 rounded-lg border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    <span>{p.h1}</span>
                    <ArrowRight className="size-4 shrink-0 text-muted-foreground group-hover:text-primary" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CompactLinkGrid;
